import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useNavContext } from "../context/nav_context";
import {
  api_v3_get_tournament_by_id,
  api_v3_get_matches_by_tournament_by_states,
  MATCH_STATUS_PENDING_DATE,
  MATCH_STATUS_NOT_STARTED,
  MATCH_STATUS_FINISHED,
  MATCH_STATUS_REVIEWED,
} from "../utils/constants";
import { TfiAngleLeft } from "react-icons/tfi";
import { Loading } from "../components";

const AdminTournamentMatches = () => {
  const { tournamentId } = useParams();
  const { user } = useNavContext();
  const [divInProgress, setDivInProgress] = useState(false);
  const [divError, setDivError] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [matchInProgress, setMatchInProgress] = useState(false);
  const [matchError, setMatchError] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selectedLeagueDay, setSelectedLeagueDay] = useState(1);
  const [selectedDivision, setSelectedDivision] = useState();
  const [selectedStat, setSelectedStat] = useState(0);

  const PEND_DATE = 0, PEND_GAME = 1, PEND_REV = 2, REVIEW = 3;

  useEffect(() => {
    const loadDivisions = async () => {
      try {
        setDivInProgress(true);
        const response = await axios.get(api_v3_get_tournament_by_id(tournamentId));
        if (response.status === 200) {
          setDivisions(response.data.divisions.sort((a, b) => (a.sequence > b.sequence ? 1 : -1)));
          setDivInProgress(false); setDivError(false);
        } else { setDivInProgress(false); setDivError(true); }
      } catch (error) { setDivInProgress(false); setDivError(true); }
    };
    const loadMatches = async () => {
      setMatchInProgress(true);
      try {
        const response = await axios.get(api_v3_get_matches_by_tournament_by_states(tournamentId, `${MATCH_STATUS_PENDING_DATE},${MATCH_STATUS_NOT_STARTED},${MATCH_STATUS_FINISHED},${MATCH_STATUS_REVIEWED}`));
        if (response.status === 200) {
          setMatches(response.data.filter((m) => m.playerAusername !== "DESCANSA" && m.playerBusername !== "DESCANSA"));
          setMatchInProgress(false); return true;
        } else { setMatchInProgress(false); setMatchError(true); }
      } catch (error) {
        if (error.response && error.response.status === 404) { setMatchError(false); setMatches([]); setMatchInProgress(false); }
        else { setMatchInProgress(false); setMatchError(true); }
      }
    };
    if (tournamentId) { loadDivisions(); loadMatches(); }
  }, [tournamentId]);

  const filteredMatches = selectedDivision
    ? matches.filter((m) => {
        const dayMatch = m.leagueDay === selectedLeagueDay;
        const divMatch = m.divisionId === selectedDivision;
        if (selectedStat === PEND_DATE) return dayMatch && divMatch && m.status === MATCH_STATUS_PENDING_DATE;
        if (selectedStat === PEND_GAME) return dayMatch && divMatch && m.status === MATCH_STATUS_NOT_STARTED;
        if (selectedStat === PEND_REV) return dayMatch && divMatch && m.status === MATCH_STATUS_FINISHED;
        if (selectedStat === REVIEW) return dayMatch && divMatch && m.status === MATCH_STATUS_REVIEWED;
        return dayMatch && divMatch;
      })
    : [];

  const counts = {};
  [PEND_DATE, PEND_GAME, PEND_REV, REVIEW].forEach((s) => {
    counts[s] = selectedDivision
      ? matches.filter((m) => m.divisionId === selectedDivision && m.leagueDay === selectedLeagueDay && (
          s === PEND_DATE ? m.status === MATCH_STATUS_PENDING_DATE :
          s === PEND_GAME ? m.status === MATCH_STATUS_NOT_STARTED :
          s === PEND_REV ? m.status === MATCH_STATUS_FINISHED :
          m.status === MATCH_STATUS_REVIEWED
        )).length
      : 0;
  });
  const totalMatches = selectedDivision
    ? matches.filter((m) => m.divisionId === selectedDivision && m.leagueDay === selectedLeagueDay).length
    : 0;

  if (!user) return <Canvas><EmptyState>Debes iniciar sesión para acceder a esta página.</EmptyState></Canvas>;
  if (!user.isAdmin && !user.isModerator) return <Canvas><EmptyState>Lo siento majete pero estás intentando acceder a donde no debes.</EmptyState></Canvas>;
  if (divInProgress || matchInProgress) return <Canvas><Loading /></Canvas>;
  if (divError || matchError) return <Canvas><EmptyState>Ha ocurrido un error cargando las divisiones y los partidos</EmptyState></Canvas>;

  return (
    <Canvas>
      <Content>
        <HeaderRow>
          <PageTitle>ADMIN PARTIDOS</PageTitle>
          <BackLink to="/admin/"><TfiAngleLeft /> ADMIN HOME</BackLink>
        </HeaderRow>

        {!selectedDivision ? (
          <>
            <PromptBanner>SELECCIONA UNA DIVISIÓN</PromptBanner>
            <DivIconRow>
              {divisions.map((div) => (
                <DivIcon key={div.divisionId} onClick={() => setSelectedDivision(div.divisionId)}>
                  <DivIconImg src={div.image} alt={div.name} />
                  <DivIconLabel>{div.name}</DivIconLabel>
                </DivIcon>
              ))}
            </DivIconRow>
          </>
        ) : (
          <>
            <DivIconRow>
              {divisions.map((div) => (
                <DivIcon key={div.divisionId} $active={selectedDivision === div.divisionId} onClick={() => setSelectedDivision(div.divisionId)}>
                  <DivIconImg src={div.image} alt={div.name} $active={selectedDivision === div.divisionId} />
                </DivIcon>
              ))}
            </DivIconRow>

            <DayRow>
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <DayPill key={day} $active={selectedLeagueDay === day} onClick={() => setSelectedLeagueDay(day)}>
                  J{day}
                </DayPill>
              ))}
            </DayRow>

            <StatsRow>
              <StatCard $active={selectedStat === -1} onClick={() => setSelectedStat(-1)}>
                <StatNum>{totalMatches}</StatNum>
                <StatLabel>TOTAL</StatLabel>
              </StatCard>
              <StatCard $active={selectedStat === PEND_DATE} onClick={() => setSelectedStat(PEND_DATE)} $col="#ff9f43">
                <StatNum>{counts[PEND_DATE]}</StatNum>
                <StatLabel>PEND. FECHA</StatLabel>
              </StatCard>
              <StatCard $active={selectedStat === PEND_GAME} onClick={() => setSelectedStat(PEND_GAME)} $col="#3860be">
                <StatNum>{counts[PEND_GAME]}</StatNum>
                <StatLabel>PEND. JUGAR</StatLabel>
              </StatCard>
              <StatCard $active={selectedStat === PEND_REV} onClick={() => setSelectedStat(PEND_REV)} $col="var(--v-uv)">
                <StatNum>{counts[PEND_REV]}</StatNum>
                <StatLabel>PEND. REVISAR</StatLabel>
              </StatCard>
              <StatCard $active={selectedStat === REVIEW} onClick={() => setSelectedStat(REVIEW)} $col="var(--v-mint)">
                <StatNum>{counts[REVIEW]}</StatNum>
                <StatLabel>REVISADOS</StatLabel>
              </StatCard>
            </StatsRow>

            <MatchGrid>
              {filteredMatches.length > 0 ? (
                filteredMatches.map((match) => (
                  <MatchCard key={match.matchId} to={`/match/${match.matchId}/detail`}>
                    <MatchPlayerRow>
                      <MatchPlayer $win={
                        match.playerAresult > match.playerBresult || match.playerAaw === "AW"
                      }>{match.playerAusername}</MatchPlayer>
                      <MatchScore>
                        {match.status === MATCH_STATUS_NOT_STARTED || match.status === MATCH_STATUS_PENDING_DATE
                          ? "?"
                          : `${match.playerAaw || ""} ${match.playerAresult ?? ""}`.trim() || "?"}
                      </MatchScore>
                    </MatchPlayerRow>
                    <MatchPlayerRow>
                      <MatchPlayer $win={
                        match.playerBresult > match.playerAresult || match.playerBaw === "AW"
                      }>{match.playerBusername}</MatchPlayer>
                      <MatchScore>
                        {match.status === MATCH_STATUS_NOT_STARTED || match.status === MATCH_STATUS_PENDING_DATE
                          ? "?"
                          : `${match.playerBaw || ""} ${match.playerBresult ?? ""}`.trim() || "?"}
                      </MatchScore>
                    </MatchPlayerRow>
                    {match.matchDate && <MatchDate>{new Date(match.matchDate).toLocaleDateString("es-ES")}</MatchDate>}
                  </MatchCard>
                ))
              ) : (
                <EmptyMatches>No hay partidos para este día.</EmptyMatches>
              )}
            </MatchGrid>
          </>
        )}
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`background: var(--v-canvas); min-height: 100vh; display: flex; justify-content: center; padding: 4rem 1.5rem;`;
const Content = styled.div`width: 100%; max-width: 900px;`;
const PageTitle = styled.h1`font-family: Anton, sans-serif; font-size: clamp(36px, 6vw, 56px); font-weight: 400; text-transform: uppercase; line-height: 1; color: var(--v-fg); margin: 0;`;
const EmptyState = styled.p`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg-muted); text-align: center; padding: 4rem 1rem;`;
const HeaderRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 2.5rem; flex-wrap: wrap; gap: 1rem;`;
const BackLink = styled(Link)`display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-mint); text-decoration: none; padding: 0.5rem 0.9rem; border: 1px solid rgba(60,255,208,0.2); border-radius: 24px; transition: background 0.15s ease; &:hover { background: rgba(60,255,208,0.04); }`;
const PromptBanner = styled.div`background: rgba(82,0,255,0.08); border: 1px solid rgba(82,0,255,0.15); color: var(--v-uv); font-family: var(--v-font-mono); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; padding: 0.75rem; border-radius: 12px; text-align: center; margin-bottom: 1.5rem;`;

const DivIconRow = styled.div`display: flex; flex-wrap: wrap; gap: 0.75rem; justify-content: center; margin-bottom: 1.5rem;`;
const DivIcon = styled.div`display: flex; flex-direction: column; align-items: center; gap: 0.3rem; cursor: pointer;`;
const DivIconImg = styled.img`width: 3.5rem; height: 3.5rem; object-fit: contain; border-radius: 12px; border: 2px solid ${({ $active }) => $active ? "var(--v-mint)" : "transparent"}; filter: ${({ $active }) => $active ? "none" : "grayscale(100%)"}; opacity: ${({ $active }) => $active ? 1 : 0.4}; transition: all 0.2s ease; ${DivIcon}:hover & { filter: none; opacity: 1; }`;
const DivIconLabel = styled.span`font-family: var(--v-font-mono); font-size: 8px; font-weight: 600; text-transform: uppercase; color: var(--v-fg-muted);`;

const DayRow = styled.div`display: flex; gap: 6px; margin-bottom: 1.25rem; flex-wrap: wrap;`;
const DayPill = styled.button`padding: 0.4rem 0.9rem; border: 1px solid ${({ $active }) => $active ? "var(--v-mint)" : "rgba(255,255,255,0.08)"}; background: ${({ $active }) => $active ? "rgba(60,255,208,0.08)" : "transparent"}; color: ${({ $active }) => $active ? "var(--v-mint)" : "var(--v-fg)"}; font-family: var(--v-font-mono); font-size: 11px; font-weight: 600; border-radius: 20px; cursor: pointer; transition: all 0.15s ease; &:hover { border-color: var(--v-mint); }`;

const StatsRow = styled.div`display: flex; gap: 6px; margin-bottom: 1.5rem; flex-wrap: wrap;`;
const StatCard = styled.div`flex: 1; min-width: 80px; background: var(--v-surface); border: 1px solid ${({ $active }) => $active ? "var(--v-mint)" : "rgba(255,255,255,0.06)"}; border-radius: 16px; padding: 0.75rem; text-align: center; cursor: pointer; transition: border-color 0.15s ease; &:hover { border-color: var(--v-mint); }`;
const StatNum = styled.div`font-family: var(--v-font-body); font-size: 28px; font-weight: 700; color: var(--v-fg);`;
const StatLabel = styled.div`font-family: var(--v-font-mono); font-size: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); margin-top: 2px;`;

const MatchGrid = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const MatchCard = styled(Link)`display: flex; align-items: center; gap: 1rem; padding: 0.65rem 1rem; background: var(--v-surface); border: 1px solid rgba(255,255,255,0.06); border-radius: 16px; text-decoration: none; transition: border-color 0.15s ease; &:hover { border-color: rgba(60,255,208,0.12); }`;
const MatchPlayerRow = styled.div`display: flex; align-items: center; gap: 0.5rem; flex: 1;`;
const MatchPlayer = styled.span`font-family: var(--v-font-body); font-size: 13px; font-weight: ${({ $win }) => $win ? 700 : 400}; color: ${({ $win }) => $win ? "var(--v-mint)" : "var(--v-fg)"}; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const MatchScore = styled.span`font-family: var(--v-font-mono); font-size: 12px; font-weight: 600; color: var(--v-fg-muted); min-width: 2rem; text-align: right;`;
const MatchDate = styled.span`font-family: var(--v-font-mono); font-size: 9px; color: var(--v-fg-muted); flex-shrink: 0;`;
const EmptyMatches = styled.p`font-family: var(--v-font-body); font-size: 13px; color: var(--v-fg-muted); text-align: center; padding: 2rem;`;

export default AdminTournamentMatches;
